import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, ChevronRight } from "lucide-react";

import HeaderBar from "@/components/HeaderBar";
import SponsorBanner from "@/components/SponsorBanner";
import UploadCard from "@/components/UploadCard";
import PatientCard from "@/components/PatientCard";
import BiometryGrid from "@/components/BiometryGrid";
import ResultsSection from "@/components/ResultsSection";
import ReportCard from "@/components/ReportCard";
import AlWarningBanner from "@/components/AlWarningBanner";
import FooterBar from "@/components/FooterBar";
import PartnerStrip from "@/components/PartnerStrip";
import HistoryDrawer from "@/components/HistoryDrawer";
import OutcomesChart from "@/components/OutcomesChart";

import { Button } from "@/components/ui/button";
import { calculate, extractBiometry, saveHistory, downloadReportPdf } from "@/lib/api";
import { buildCalculationPayload, mapExtraction } from "@/lib/calculation";
import { useSurgeonFactor } from "@/hooks/useSurgeonFactor";
import { log } from "@/lib/log";
import { SAMPLES } from "@/lib/samples";
import { DEFAULT_LENS_ID, LENS_MODELS } from "@/lib/lenses";

const EMPTY_EYE = { k1: "", k1_axis: "", k2: "", k2_axis: "", al: "", acd: "", lt: "", wtw: "" };
const DEFAULT_LENS = LENS_MODELS.find((l) => l.id === DEFAULT_LENS_ID);

export default function CalculatorPage() {
  const [patient, setPatient] = useState({
    patient_name: "",
    patient_id: "",
    age: "",
    prior_surgery: "none",
    target_refraction: 0,
    a_constant: DEFAULT_LENS?.a_constant ?? 119.84,
    a_constant_other: DEFAULT_LENS?.a_constant_other ?? 119.74,
    hoffer_pacd: DEFAULT_LENS?.hoffer_pacd ?? "",
    c_constant: 0.5,
    lens_id: DEFAULT_LENS_ID,
    sia: 0.2,
    sia_axis: 180,
  });
  const [od, setOd] = useState({ ...EMPTY_EYE });
  const [os, setOs] = useState({ ...EMPTY_EYE });
  const [extracting, setExtracting] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showTrends, setShowTrends] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [extractInfo, setExtractInfo] = useState(null);
  const sfState = useSurgeonFactor(patient.lens_id, setPatient);
  const { sf, currentLensSF, refresh: refreshSF } = sfState;

  const alDiff = useMemo(() => {
    const a = parseFloat(od.al), b = parseFloat(os.al);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return Math.abs(a - b);
  }, [od.al, os.al]);

  const loadSample = (key) => {
    const s = SAMPLES[key];
    setPatient((p) => ({
      ...p,
      patient_name: s.patient_name,
      age: s.age,
      prior_surgery: s.prior_surgery,
      target_refraction: s.target_refraction,
    }));
    setOd({ ...s.od });
    setOs({ ...s.os });
    setResult(null);
    toast.success(`Loaded ${s.label}`);
  };

  const handleUpload = async (file) => {
    setExtracting(true);
    setExtractInfo(null);
    try {
      const { od: odV, os: osV, meta, info } = mapExtraction(await extractBiometry(file), EMPTY_EYE);
      if (odV) setOd(odV);
      if (osV) setOs(osV);
      setPatient((p) => ({
        ...p,
        patient_name: meta.patient_name || p.patient_name,
        patient_id: meta.patient_id || p.patient_id,
        age: meta.age ?? p.age,
      }));
      setExtractInfo(info);
      toast.success(`Extracted ${info.eyes.join(" + ") || "no eyes"}${info.device ? ` from ${info.device}` : ""}. Please verify values.`);
    } catch (e) {
      log.error("Extraction failed", e);
      toast.error(e?.response?.data?.detail || "Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const runCalculate = async () => {
    const payload = buildCalculationPayload(patient, od, os);
    if (!payload.od && !payload.os) {
      toast.error("Enter biometry for at least one eye");
      return;
    }
    setCalculating(true);
    try {
      const data = await calculate(payload);
      setResult({ request: payload, result: data });
      toast.success("Calculation complete");
      // auto-save history
      saveHistory({
        patient_name: payload.patient_name || "Unknown",
        patient_id: payload.patient_id || "",
        request: payload,
        result: data,
      }).then(() => refreshSF()).catch((err) => log.error("History auto-save failed", err));
      window.setTimeout(() => {
        document.getElementById("results-section")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (e) {
      log.error("Calculation failed", e);
      toast.error(e?.response?.data?.detail || "Calculation failed");
    } finally {
      setCalculating(false);
    }
  };

  const handlePrint = () => window.print();

  const handleDownloadPdf = async () => {
    if (!result) return;
    setDownloading(true);
    try {
      await downloadReportPdf(result.request, result.result);
      toast.success("PDF report downloaded");
    } catch (err) {
      log.error("PDF export failed", err);
      toast.error("PDF export failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--ink))]">
      <HeaderBar
        onOpenHistory={() => setShowHistory(true)}
        onPrint={handlePrint}
        onLoadSample={loadSample}
        surgeonFactor={sf}
        currentLensSF={currentLensSF}
        currentLensName={LENS_MODELS.find((l) => l.id === patient.lens_id)?.name}
        onApplySF={sfState.applyGlobal}
        onApplyLensSF={sfState.applyLens}
        onApplyLensC={sfState.applyLensC}
        onOpenTrends={() => setShowTrends(true)}
      />

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-6">
        <SponsorBanner />
      </div>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 space-y-6 no-print">
        <section className="reveal">
          <UploadCard
            onFile={handleUpload}
            loading={extracting}
            info={extractInfo}
            onLoadSample={loadSample}
          />
        </section>

        <section className="reveal reveal-delay-1">
          <PatientCard patient={patient} setPatient={setPatient} />
        </section>

        <AlWarningBanner alDiff={alDiff} />

        <section className="reveal reveal-delay-2">
          <BiometryGrid od={od} os={os} setOd={setOd} setOs={setOs} />
        </section>

        <div className="flex flex-wrap items-center justify-end gap-3">
          <div className="text-sm text-slate-500 mr-auto flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <Sparkles className="w-4 h-4 text-sky-600" />
            <span>Barrett A-const <span className="font-mono">{patient.a_constant}</span></span>
            {patient.a_constant_other && String(patient.a_constant_other) !== String(patient.a_constant) && (
              <span>· other formulas <span className="font-mono">{patient.a_constant_other}</span></span>
            )}
            <span>· SIA <span className="font-mono">{patient.sia}</span> D @ <span className="font-mono">{patient.sia_axis}°</span></span>
          </div>
          <Button
            variant="outline"
            data-testid="clear-btn"
            onClick={() => {
              setOd({ ...EMPTY_EYE });
              setOs({ ...EMPTY_EYE });
              setResult(null);
            }}
          >
            Clear
          </Button>
          <Button
            data-testid="calculate-btn"
            onClick={runCalculate}
            disabled={calculating}
            className="bg-sky-700 hover:bg-sky-800 text-white"
          >
            {calculating ? "Calculating…" : "Calculate IOL Power"}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        <div id="results-section" />

        <ResultsSection result={result} onPrint={handlePrint} onDownloadPdf={handleDownloadPdf} downloading={downloading} />
        <PartnerStrip />
      </main>

      {result && (
        <div className="print-only">
          <ReportCard result={result.result} request={result.request} printMode />
        </div>
      )}

      <FooterBar />

      <OutcomesChart open={showTrends} onOpenChange={setShowTrends} defaultLensId={patient.lens_id} />

      <HistoryDrawer
        open={showHistory}
        onOpenChange={setShowHistory}
        onChanged={refreshSF}
        onOpenTrends={() => { setShowHistory(false); setShowTrends(true); }}
        onLoad={(entry) => {
          setPatient((p) => ({ ...p, ...entry.request, target_refraction: entry.request.target_refraction ?? 0 }));
          if (entry.request.od) setOd({ ...EMPTY_EYE, ...entry.request.od });
          if (entry.request.os) setOs({ ...EMPTY_EYE, ...entry.request.os });
          setResult({ request: entry.request, result: entry.result });
          setShowHistory(false);
        }}
      />
    </div>
  );
}
